export const SITE_ORIGIN = 'https://fatalitystudio.ru';

const ARTICLE_PATHS = new Set([
  '/blog/digital-trends-2024/',
  '/blog/mobile-roi/',
  '/blog/seo-top3/',
]);

const CASE_PATHS = new Set([
  '/portfolio/fitness-course-landing/',
  '/portfolio/sokolniki-restaurant/',
  '/portfolio/tech-store/',
]);

export function normalizeRoutePath(pathname = '/') {
  const path = pathname.split(/[?#]/, 1)[0] || '/';
  return path === '/' ? '/' : `/${path.replace(/^\/+|\/+$/g, '')}/`;
}

export function buildCanonical(pathname) {
  return `${SITE_ORIGIN}${normalizeRoutePath(pathname)}`;
}

function structuredDataEntries(structuredData) {
  if (!structuredData) return [];
  if (Array.isArray(structuredData)) return structuredData;
  if (Array.isArray(structuredData['@graph'])) return structuredData['@graph'];
  return [structuredData];
}

function normalizeVisibleText(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

export function appendVisibleFaqStructuredData(structuredData, pathname, documentNode) {
  const normalizedPath = normalizeRoutePath(pathname);
  const entries = structuredDataEntries(structuredData);
  if (
    !normalizedPath.startsWith('/services/') ||
    entries.some((entry) => entry?.['@type'] === 'FAQPage')
  ) {
    return structuredData;
  }

  const faqEntries = Array.from(documentNode.querySelectorAll('.faq-item'), (faqItem) => {
    const questionNode = faqItem.querySelector('.faq-q')?.cloneNode(true);
    questionNode?.querySelectorAll('.faq-toggle').forEach((node) => node.remove());
    const answerNode = faqItem.querySelector('.faq-a-inner');
    const question = normalizeVisibleText(questionNode?.textContent);
    const answer = normalizeVisibleText(answerNode?.innerText || answerNode?.textContent);
    return question && answer
      ? {
          '@type': 'Question',
          name: question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: answer,
          },
        }
      : null;
  }).filter(Boolean);

  if (faqEntries.length === 0) return structuredData;

  return [
    ...entries,
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqEntries,
    },
  ];
}

export function buildRouteStructuredData({ pathname, title, description, canonical, structuredData }) {
  if (structuredData) return structuredData;

  const normalizedPath = normalizeRoutePath(pathname);
  const common = {
    '@context': 'https://schema.org',
    name: title,
    description,
    url: canonical,
  };

  if (ARTICLE_PATHS.has(normalizedPath)) {
    return {
      ...common,
      '@type': 'Article',
      headline: title,
      mainEntityOfPage: canonical,
      publisher: {
        '@type': 'Organization',
        name: 'Fatality Studio',
        url: SITE_ORIGIN,
      },
    };
  }

  if (CASE_PATHS.has(normalizedPath)) {
    return {
      ...common,
      '@type': 'CreativeWork',
      creator: {
        '@type': 'Organization',
        name: 'Fatality Studio',
        url: SITE_ORIGIN,
      },
    };
  }

  // Страница контактов уже рендерит собственную LocalBusiness-разметку.
  if (normalizedPath === '/contacts/') return null;

  if (normalizedPath === '/') {
    return {
      ...common,
      '@type': 'WebSite',
    };
  }

  if (normalizedPath.startsWith('/services/')) {
    return {
      ...common,
      '@type': 'Service',
      provider: {
        '@type': 'Organization',
        name: 'Fatality Studio',
        url: SITE_ORIGIN,
      },
    };
  }

  if (normalizedPath === '/blog/' || normalizedPath === '/portfolio/') {
    return {
      ...common,
      '@type': 'CollectionPage',
    };
  }

  return {
    ...common,
    '@type': 'WebPage',
  };
}
